import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  ServiceFeesSearchRequest,
  ServiceFeesSearchResponse,
} from '@/types/settings-api';

/**
 * Service-fees catalogue search — PROD-763 PR-B Billing Edit dialog.
 *
 * POST /api/Billing/Services/Fees?pageNumber={n}&pageSize={n}&q={search}
 *
 * Used by the inline Add Service picker inside the Billing Profile
 * Edit dialog. Flutter source: `service_code_list_widget.dart`
 * + `edit_billing_profile_widget.dart` (the `isProviderFeeSearch`
 * boolean toggles between the two `type` modes).
 *
 * Two modes via the body's `type` field:
 *
 *   type: 'provider' → returns services the practitioner has already
 *                      configured fees for. Used when filling a profile
 *                      from the provider's existing fee schedule.
 *
 *   type: 'all'      → returns the full org-wide service catalogue.
 *                      Used when picking from the broader org services.
 *
 * Wire shape verified against live dev cURLs 2026-05-15. Response is
 * a paginated envelope; `results` may ship as `null` (not `[]`) when
 * the search returns no matches — caller-side `?? []` is mandatory.
 *
 * The hook short-circuits to a disabled query when there's no provider
 * ID — same pattern as other GET hooks. Caller debounces its `q`
 * input (consistent with the 250ms debounce we use across all
 * search hooks in this module).
 */
export interface UseServiceFeesSearchArgs {
  providerId: string | null | undefined;
  /** Debounced search term — caller-managed. Empty string is allowed
   *  (returns first page of all results). */
  q: string;
  type: 'provider' | 'all';
  pageNumber?: number;
  pageSize?: number;
  /** Optional location / patient filters — pass null to match Flutter's
   *  default behaviour where these are unset on the provider-settings
   *  Add Service flow (`edit_billing_profile_widget.dart`). */
  locationId?: string | null;
  patientId?: string | null;
}

export function useServiceFeesSearch({
  providerId,
  q,
  type,
  pageNumber = 1,
  pageSize = 50,
  locationId = null,
  patientId = null,
}: UseServiceFeesSearchArgs) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: [
      'settings',
      'service-fees-search',
      providerId,
      type,
      trimmed,
      pageNumber,
      pageSize,
      locationId,
      patientId,
    ],
    queryFn: ({ signal }) => {
      if (!providerId) {
        throw new Error('useServiceFeesSearch called without providerId');
      }
      const qs = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        q: trimmed,
      });
      const body: ServiceFeesSearchRequest = {
        providerId,
        locationId,
        patientId,
        type,
      };
      return post<ServiceFeesSearchResponse>(
        `/api/Billing/Services/Fees?${qs.toString()}`,
        body,
        { signal },
      );
    },
    enabled: !!providerId,
    staleTime: 60_000,
  });
}
